import React, {createContext, useContext} from 'react'
import {GameSettings} from '@colonist/game-base-types'
import {
    ScenarioSettingEnum,
    ExtensionSettingEnum,
    GameTypeEnum,
    ModeSettingEnum,
    MapSettingEnum,
    DiceSettingEnum,
    GameSpeedSettings,
    BotSpeedSettings,
    ELOTypeEnum,
} from '@colonist/types'
import {UIGameModeProvider} from './UIGameModeContext'
import {GameModeDataController} from '@colonist/shared-types'

interface UIGameSettingsContextProps {
    roomId: string
    gameType: GameTypeEnum
    isPrivateGame: boolean
    isPlayOrderSelectionActive: boolean
    minimumKarma: number
    eloType: ELOTypeEnum

    modeSetting: ModeSettingEnum
    extensionSetting: ExtensionSettingEnum
    scenarioSetting: ScenarioSettingEnum
    mapSetting: MapSettingEnum
    diceSetting: DiceSettingEnum

    victoryPointsToWin: number
    isKarmaActive: boolean
    cardDiscardLimit: number
    maxPlayers: number
    gameSpeed: GameSpeedSettings
    botSpeed: BotSpeedSettings
    hideBankCards: boolean
    friendlyRobber: boolean
}

const UIGameSettingsContext = createContext<UIGameSettingsContextProps | undefined>(undefined)

export const useUIGameSettingsContext = () => {
    const context = useContext(UIGameSettingsContext)
    if(!context) {
        throw new Error('useUIGameSettingsContext must be used within a UIGameSettingsProvider')
    }
    return context
}

interface UIGameSettingsProviderProps {
    gameSettings: GameSettings
    children: React.ReactNode
}

export const UIGameSettingsProvider = ({
    gameSettings,
    children,
}: UIGameSettingsProviderProps) => {
    const gameSettingsProps: UIGameSettingsContextProps = {
        roomId: gameSettings.id,
        gameType: gameSettings.gameType,
        isPrivateGame: gameSettings.privateGame,
        isPlayOrderSelectionActive: gameSettings.playOrderSelectionActive,
        minimumKarma: gameSettings.minimumKarma,
        eloType: gameSettings.eloType,

        modeSetting: gameSettings.modeSetting,
        extensionSetting: gameSettings.extensionSetting,
        scenarioSetting: gameSettings.scenarioSetting,
        mapSetting: gameSettings.mapSetting,
        diceSetting: gameSettings.diceSetting,

        victoryPointsToWin: gameSettings.victoryPointsToWin,
        isKarmaActive: gameSettings.karmaActive,
        cardDiscardLimit: gameSettings.cardDiscardLimit,
        maxPlayers: gameSettings.maxPlayers,
        gameSpeed: gameSettings.gameSpeed,
        botSpeed: gameSettings.botSpeed,
        hideBankCards: gameSettings.hideBankCards,
        friendlyRobber: gameSettings.friendlyRobber,
    }

    const gameModeData = GameModeDataController.getModeDataForMode(gameSettings.modeSetting)

    return (
        <UIGameSettingsContext.Provider value={gameSettingsProps}>
            {gameModeData && (
                <UIGameModeProvider gameModeData={gameModeData}>
                    {children}
                </UIGameModeProvider>
            )}
        </UIGameSettingsContext.Provider>
    )
}
